<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import { onDestroy, untrack } from "svelte";
  import maplibregl from "maplibre-gl";
  import maplibreStyles from "maplibre-gl/dist/maplibre-gl.css?inline";
  import { NavigationControl } from "./NavigationControl.js";
  import { calculateBounds } from "$utils/geo-bounds.js";

  // Layer and source identifiers
  const SOURCE_ID = "boundaries";
  const FILL_LAYER_ID = "boundaries-fill";
  const LINE_LAYER_ID = "boundaries-line";
  const OUTLINE_SOURCE_ID = "outlines";
  const OUTLINE_LAYER_ID = "outline-line";
  const DEFAULT_COLOR = "#d9d9d9";

  /**
   * Props
   */

  /**
   * @typedef {Object} MapEventDetail
   * @property {string} geoid - The geographic identifier
   * @property {Object} properties - Feature properties from GeoJSON
   * @property {MouseEvent} event - The original mouse event
   */

  /**
   * Props for the Map component
   * @type {{
   *   features: any,
   *   outlineFeatures?: any,
   *   data?: Record<string, any>,
   *   fill?: (geoid: string) => string,
   *   stroke?: string,
   *   strokeWidth?: number,
   *   hoverStroke?: string,
   *   hoverStrokeWidth?: number,
   *   highlightFeature?: { geoid: string } | null,
   *   zoomTarget?: { geoid: string } | null,
   *   zoomable?: boolean,
   *   aspectRatio?: number,
   *   onready?: () => void,
   *   onmousemove?: (detail: MapEventDetail) => void,
   *   onmouseout?: () => void,
   *   onclick?: (detail: MapEventDetail) => void,
   *   ariaLabel?: string,
   * }}
   */
  let {
    features,
    outlineFeatures = null,
    data = {},
    fill = () => DEFAULT_COLOR,
    stroke = "#ffffff",
    strokeWidth = 0.5,
    hoverStroke = "#ffcc00",
    hoverStrokeWidth = 2,
    highlightFeature = null,
    zoomTarget = null,
    zoomable = true,
    aspectRatio = 4 / 2.3333,
    onready = () => {},
    onmousemove = () => {},
    onmouseout = () => {},
    onclick = () => {},
    ariaLabel = "Interactive map"
  } = $props();

  /**
   * Local state
   */

  // Map container element
  let mapContainer = $state();

  // Inject MapLibre CSS into the component's root node (shadow DOM or document head).
  // Using ?inline import + manual injection ensures the styles reach the shadow root
  // when this component is compiled as a web component, where document.head styles
  // cannot penetrate the shadow boundary.
  $effect(() => {
    if (!mapContainer) return;
    const root = mapContainer.getRootNode();
    const target = root.host ? root : (root.head ?? root.ownerDocument.head);
    if (!target.querySelector("#maplibregl-styles")) {
      const style = document.createElement("style");
      style.id = "maplibregl-styles";
      style.textContent = maplibreStyles;
      target.appendChild(style);
    }
  });

  // MapLibre map instance
  let map = /** @type {any} */ (null);
  // Loading state (set to false since features are provided externally)
  let isLoading = $state(false);
  // Error state
  let error = $state(null);
  // Currently hovered feature ID
  let hoveredFeatureId = /** @type {any} */ (null);
  // Previously highlighted feature ID
  let previousHighlightId = /** @type {any} */ (null);
  // Navigation control reference
  let navigationControl = /** @type {any} */ (null);

  // Outer container element (for ResizeObserver)
  let outerContainer = $state();
  // ResizeObserver for syncing MapLibre canvas to outer container size
  let resizeObserver = /** @type {ResizeObserver | null} */ (null);

  /**
   * Build color map from data object and fill function
   */
  function buildColorMap() {
    const colorMap = /** @type {Record<string, any>} */ ({});

    if (!features) return colorMap;

    // Process all features
    features.features.forEach((/** @type {any} */ feature) => {
      const geoid = feature.properties.GEOID;
      if (geoid && data && Object.hasOwn(data, geoid)) {
        colorMap[geoid] = fill(geoid);
      }
    });

    return colorMap;
  }

  /**
   * Build MapLibre expression for fill color based on data and fill function
   * @returns {string | any[]} MapLibre expression or default color
   */
  function buildFillColorExpression() {
    const colorMap = buildColorMap();

    if (Object.keys(colorMap).length === 0) {
      return DEFAULT_COLOR;
    }

    const expression = /** @type {any[]} */ (["case"]);
    Object.entries(colorMap).forEach(([geoid, color]) => {
      expression.push(["==", ["get", "GEOID"], geoid], color);
    });
    expression.push(DEFAULT_COLOR);

    return expression;
  }

  /**
   * Build MapLibre expression for line color with hover/highlight states
   */
  function buildLineColorExpression() {
    return [
      "case",
      ["boolean", ["feature-state", "hover"], false],
      hoverStroke,
      ["boolean", ["feature-state", "highlight"], false],
      hoverStroke,
      stroke
    ];
  }

  /**
   * Build MapLibre expression for line width with hover/highlight states
   */
  function buildLineWidthExpression() {
    return [
      "case",
      ["boolean", ["feature-state", "hover"], false],
      hoverStrokeWidth,
      ["boolean", ["feature-state", "highlight"], false],
      hoverStrokeWidth,
      strokeWidth
    ];
  }

  /**
   * Initialize the MapLibre map
   */
  function initializeMap() {
    if (!mapContainer || !features || map) return;

    try {
      const bounds = calculateBounds(features);

      // Create MapLibre map with identity projection for pre-projected Albers data
      map = new maplibregl.Map({
        container: mapContainer,
        style: {
          version: 8,
          sources: {},
          layers: [],
          glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf"
        },
        center: [0, 0],
        zoom: 0,
        interactive: zoomable,
        attributionControl: false,
        renderWorldCopies: false
      });

      map.on("load", () => {
        // Add GeoJSON source
        map.addSource(SOURCE_ID, {
          type: "geojson",
          data: features,
          promoteId: "GEOID" // Use GEOID as feature id for feature-state
        });

        // Add fill layer for choropleth
        addFillLayer();

        // Add line layer for borders
        addLineLayer();

        // Add outline layer if provided
        if (outlineFeatures) {
          addOutlineLayer();
        }

        // Fit map to bounds and prevent zooming/panning outside them
        map.fitBounds(bounds, { padding: 20, duration: 0 });
        map.setMaxBounds(bounds);

        // Set up interaction handlers
        setupInteractions();

        // Set up navigation controls based on zoomable prop
        updateInteractivity();

        // Apply any pre-existing highlight (e.g., for offscreen capture maps)
        updateHighlight();

        // Signal readiness after the first idle event (all layers painted, fitBounds done)
        map.once("idle", () => {
          onready();
        });
      });
    } catch (/** @type {any} */ err) {
      console.error("Error initializing map:", err);
      error = err.message;
    }
  }

  /**
   * Add the fill layer with choropleth styling
   */
  function addFillLayer() {
    if (!map || !map.getSource(SOURCE_ID)) return;

    if (map.getLayer(FILL_LAYER_ID)) {
      map.removeLayer(FILL_LAYER_ID);
    }

    map.addLayer({
      id: FILL_LAYER_ID,
      type: "fill",
      source: SOURCE_ID,
      paint: {
        "fill-color": buildFillColorExpression(),
        "fill-opacity": 1
      }
    });
  }

  /**
   * Add the line layer for borders
   */
  function addLineLayer() {
    if (!map || !map.getSource(SOURCE_ID)) return;

    if (map.getLayer(LINE_LAYER_ID)) {
      map.removeLayer(LINE_LAYER_ID);
    }

    map.addLayer({
      id: LINE_LAYER_ID,
      type: "line",
      source: SOURCE_ID,
      paint: {
        "line-color": buildLineColorExpression(),
        "line-width": buildLineWidthExpression()
      }
    });
  }

  /**
   * Add the outline layer for state boundaries (when showing districts)
   */
  function addOutlineLayer() {
    if (!map || !outlineFeatures) return;

    // Add outline source if not already present
    if (!map.getSource(OUTLINE_SOURCE_ID)) {
      map.addSource(OUTLINE_SOURCE_ID, {
        type: "geojson",
        data: outlineFeatures
      });
    }

    // Remove outline layer if it exists
    if (map.getLayer(OUTLINE_LAYER_ID)) {
      map.removeLayer(OUTLINE_LAYER_ID);
    }

    // Add outline layer
    map.addLayer({
      id: OUTLINE_LAYER_ID,
      type: "line",
      source: OUTLINE_SOURCE_ID,
      paint: {
        "line-color": stroke,
        "line-width": strokeWidth * 2 // Slightly thicker for visibility
      }
    });
  }

  /**
   * Set up mouse interaction handlers
   */
  function setupInteractions() {
    if (!map) return;

    // Hover handling
    map.on("mousemove", FILL_LAYER_ID, (/** @type {any} */ e) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const geoid = feature.properties.GEOID;

        // Update cursor
        map.getCanvas().style.cursor = "pointer";

        // Clear previous hover state
        if (hoveredFeatureId !== null && hoveredFeatureId !== geoid) {
          map.setFeatureState({ source: SOURCE_ID, id: hoveredFeatureId }, { hover: false });
        }

        // Set new hover state
        hoveredFeatureId = geoid;
        map.setFeatureState({ source: SOURCE_ID, id: geoid }, { hover: true });

        // Call mousemove callback
        onmousemove({
          geoid,
          properties: feature.properties,
          event: e.originalEvent
        });
      }
    });

    // Mouse leave handling
    map.on("mouseleave", FILL_LAYER_ID, () => {
      map.getCanvas().style.cursor = "";

      if (hoveredFeatureId !== null) {
        map.setFeatureState({ source: SOURCE_ID, id: hoveredFeatureId }, { hover: false });
        hoveredFeatureId = null;
      }

      onmouseout();
    });

    // Click handling
    map.on("click", FILL_LAYER_ID, (/** @type {any} */ e) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const geoid = feature.properties.GEOID;

        onclick({
          geoid,
          properties: feature.properties,
          event: e.originalEvent
        });
      }
    });
  }

  /**
   * Update fill layer when data or fill function changes
   */
  function updateFillLayer() {
    if (!map || !map.getLayer(FILL_LAYER_ID)) return;
    map.setPaintProperty(FILL_LAYER_ID, "fill-color", buildFillColorExpression());
  }

  /**
   * Update line layer styling
   */
  function updateLineLayer() {
    if (!map || !map.getLayer(LINE_LAYER_ID)) return;

    map.setPaintProperty(LINE_LAYER_ID, "line-color", buildLineColorExpression());
    map.setPaintProperty(LINE_LAYER_ID, "line-width", buildLineWidthExpression());
  }

  /**
   * Update highlight feature state
   */
  function updateHighlight() {
    if (!map || !map.getSource(SOURCE_ID)) return;

    // Clear previous highlight
    if (previousHighlightId !== null) {
      map.setFeatureState({ source: SOURCE_ID, id: previousHighlightId }, { highlight: false });
    }

    // Set new highlight
    if (highlightFeature && highlightFeature.geoid) {
      map.setFeatureState({ source: SOURCE_ID, id: highlightFeature.geoid }, { highlight: true });
      previousHighlightId = highlightFeature.geoid;
    } else {
      previousHighlightId = null;
    }
  }

  /**
   * Update map interactivity
   */
  function updateInteractivity() {
    if (!map) return;

    if (zoomable) {
      map.scrollZoom.enable();
      map.boxZoom.enable();
      map.dragPan.enable();
      map.doubleClickZoom.enable();
      map.touchZoomRotate.enable();

      // Add custom navigation control if not already present
      if (!navigationControl) {
        const bounds = /** @type {[[number, number], [number, number]]} */ (
          calculateBounds(features)
        );

        /**
         * Compute the bounding-box center of the selected feature in projected coordinates.
         * Returns null when there is no valid selection.
         * @returns {{ lng: number, lat: number } | null}
         */
        function getTargetCenter() {
          const geoid = zoomTarget?.geoid;
          if (!geoid) return null;

          const feature = features.features.find(
            (/** @type {any} */ f) => f.properties?.GEOID === geoid
          );
          if (!feature) return null;

          const featureBounds = calculateBounds({ features: [feature] });
          const centerX = (featureBounds[0][0] + featureBounds[1][0]) / 2;
          const centerY = (featureBounds[0][1] + featureBounds[1][1]) / 2;

          // The data uses pre-projected Albers coordinates treated as lng/lat by MapLibre,
          // so we can pass the center directly as a LngLatLike.
          return { lng: centerX, lat: centerY };
        }

        // Center of the full feature collection bounds, used to lerp back toward on zoom-out.
        const boundsCenter = {
          lng: (bounds[0][0] + bounds[1][0]) / 2,
          lat: (bounds[0][1] + bounds[1][1]) / 2
        };

        navigationControl = new NavigationControl(
          /** @type {any} */ ({
            bounds,
            fitBoundsOptions: { padding: 20, duration: 300 },
            onZoomIn: (/** @type {any} */ map) => {
              const target = getTargetCenter();
              if (target) {
                const currentCenter = map.getCenter();
                // Blend factor: how far to move toward the target center per zoom step.
                // 0.0 = no panning, 1.0 = snap to target immediately. Tune as needed.
                const blend = 0.7;
                map.easeTo({
                  center: {
                    lng: currentCenter.lng + (target.lng - currentCenter.lng) * blend,
                    lat: currentCenter.lat + (target.lat - currentCenter.lat) * blend
                  },
                  zoom: map.getZoom() + 1,
                  duration: 200,
                  // Linear easing keeps zoom and pan advancing at the same rate, preventing
                  // the compounding deceleration that makes repeated clicks feel jerky.
                  easing: (/** @type {number} */ t) => t
                });
              } else {
                map.zoomIn();
              }
            },
            onZoomOut: (/** @type {any} */ map) => {
              const target = getTargetCenter();
              if (target) {
                const currentCenter = map.getCenter();
                // On zoom-out, lerp back toward the overall bounds center so the view
                // gradually recovers the full context rather than staying locked on the edge.
                const blend = 0.2;
                map.easeTo({
                  center: {
                    lng: currentCenter.lng + (boundsCenter.lng - currentCenter.lng) * blend,
                    lat: currentCenter.lat + (boundsCenter.lat - currentCenter.lat) * blend
                  },
                  zoom: map.getZoom() - 1,
                  duration: 200,
                  easing: (/** @type {number} */ t) => t
                });
              } else {
                map.zoomOut();
              }
            }
          })
        );
        map.addControl(navigationControl, "bottom-right");
      }
    } else {
      map.scrollZoom.disable();
      map.boxZoom.disable();
      map.dragPan.disable();
      map.doubleClickZoom.disable();
      map.touchZoomRotate.disable();

      // Remove navigation control if present
      if (navigationControl) {
        map.removeControl(navigationControl);
        navigationControl = null;
      }
    }
  }

  /**
   * Lifecycle: Clean up and reinitialize when features change
   */
  $effect(() => {
    // Track features as a dependency
    features;

    // Clean up existing map when features change (use untrack to avoid making map a dependency)
    untrack(() => {
      if (map) {
        map.remove();
        map = null;
        hoveredFeatureId = null;
        previousHighlightId = null;
        navigationControl = null;
      }
    });
  });

  /**
   * Lifecycle: Initialize map when container and features are ready
   */
  $effect(() => {
    if (mapContainer && features && !map) {
      initializeMap();
    }
  });

  /**
   * Reactive: Update fill layer when data or fill function changes
   */
  $effect(() => {
    // Explicitly read reactive dependencies
    const _ = [data, fill];

    if (map && features) {
      updateFillLayer();
    }
  });

  /**
   * Reactive: Update line layer when stroke properties change
   */
  $effect(() => {
    // Explicitly read reactive dependencies
    const _ = [stroke, strokeWidth, hoverStroke, hoverStrokeWidth];

    if (map) {
      updateLineLayer();
    }
  });

  /**
   * Reactive: Update highlight when highlightFeature changes
   */
  $effect(() => {
    highlightFeature; // Track dependency
    if (map) {
      updateHighlight();
    }
  });

  /**
   * Reactive: Update interactivity when zoomable changes
   */
  $effect(() => {
    zoomable; // Track dependency
    if (map) {
      updateInteractivity();
    }
  });

  /**
   * Watch the outer aspect-ratio container for size changes and sync MapLibre.
   * MapLibre's built-in ResizeObserver watches .map-inner (the container passed to it),
   * but Firefox can fail to propagate aspect-ratio-derived size changes to absolutely
   * positioned children. Observing the outer container bridges this gap.
   */
  $effect(() => {
    if (!outerContainer || !map) return;

    resizeObserver = new ResizeObserver(() => {
      map.resize();
    });
    resizeObserver.observe(outerContainer);

    return () => {
      resizeObserver?.disconnect();
      resizeObserver = null;
    };
  });

  /**
   * Cleanup on destroy
   */
  onDestroy(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    if (map) {
      map.remove();
      map = null;
    }
    navigationControl = null;
  });
</script>

<div
  bind:this={outerContainer}
  class="map-container"
  role="img"
  aria-label={ariaLabel}
  style:aspect-ratio={aspectRatio}
>
  {#if isLoading}
    <div class="loading-container">
      <p>Loading map...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p>Error loading map: {error}</p>
    </div>
  {:else}
    <div bind:this={mapContainer} class="map-inner"></div>
  {/if}
</div>

<style>
  .map-container {
    position: relative;
    width: 100%;
    overflow: hidden;
  }

  .map-inner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .loading-container,
  .error-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: 18rem;
    background: var(--color-gray-shade-lightest);
  }

  .loading-container p,
  .error-container p {
    font-size: 14px;
    color: var(--color-gray-shade-dark);
  }

  .error-container p {
    color: var(--color-red);
  }
</style>
